import { Cart } from '@app/types/monolith/cart';
import {
  UpdateLineItemAPIError,
  UpdateLineItemErrorCodes,
} from '@app/types/monolith/types';
import { useUpdateCartItemsCount } from '@redbubble/next/cart-items-count';
import logger from '@redbubble/next/logger';
import { useRef, useState } from 'react';
import { CartCheckoutClient } from '../../cartCheckoutClient';
import { DispatchActionsType, StateDispatch } from '../types';

export type UpdateCartLineItemResult = {
  loading: boolean;
  cart?: Cart;
  error?: UpdateLineItemAPIError['error'];
};

export const useUpdateCartLineItem = ({
  client,
  dispatch,
}: {
  client: CartCheckoutClient;
  dispatch: StateDispatch;
}) => {
  const abortController = useRef<AbortController>();
  const updateCartItemsCount = useUpdateCartItemsCount();
  const [result, setResult] = useState<UpdateCartLineItemResult>({
    cart: undefined,
    loading: false,
    error: undefined,
  });

  const handler = async ({
    inventoryItemId,
    quantity,
  }): Promise<UpdateCartLineItemResult> => {
    if (abortController.current) {
      abortController.current.abort();
    }
    const newAbortController = new AbortController();
    abortController.current = newAbortController;

    dispatch({ type: DispatchActionsType.SET_CART_LOADING, payload: true });
    setResult((prev) => ({ ...prev, loading: true }));

    return client
      .updateCartLineItem(
        {
          id: inventoryItemId,
          quantity,
        },
        newAbortController.signal
      )
      .then((resp) => {
        if ('cart' in resp) {
          dispatch({ type: DispatchActionsType.SET_CART, payload: resp.cart });
          updateCartItemsCount(resp.cart.total_items_count);
          return {
            cart: resp.cart,
            loading: false,
            error: undefined,
          };
        }

        dispatch({
          type: DispatchActionsType.SET_CART_LOADING,
          payload: false,
        });

        return {
          cart: undefined,
          loading: false,
          error: resp.error,
        };
      })
      .catch((err) => {
        // we don't want to show error to the customer when request gets aborted.
        if (err.name === 'AbortError')
          return {
            cart: undefined,
            loading: false,
            error: undefined,
          };

        const errMessage = err?.message || String(err);
        const addCartLineItemError = `Unexpected error while updating cart line item quantity using useUpdateCartLineItem() "${errMessage}"`;

        logger.error(
          {
            err: err,
            message: addCartLineItemError,
            context: 'CartAndDeliveryDatesDataContext/useUpdateCartLineItem',
          },
          addCartLineItemError
        );

        dispatch({
          type: DispatchActionsType.SET_CART_LOADING,
          payload: false,
        });

        return {
          cart: undefined,
          loading: false,
          error: {
            error_code: UpdateLineItemErrorCodes.UnexpectedError,
          },
        };
      })
      .then((res) => {
        setResult(res);
        return res;
      });
  };

  return {
    ...result,
    abortController,
    handler,
  };
};
